import { useEffect, useState, useCallback } from 'react';
import { BookContent } from '../type';
import { useEditedWidget } from 'features/Rooms/PersonalMeetingCorner/states/hooks';

interface BookState {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    isSearching: boolean;
    searchResults: BookContent[];
    selectBook: (book: BookContent) => void;
    selectedBook?: BookContent;
    searchBooks: () => void;
    clearSearchResults: () => void;
    error: string;
    setError: (error: string) => void;
    preserveAspectRatio: boolean;
    setPreserveAspectRatio: (preserve: boolean) => void;
}

export const useBookState = (onBookSelect: (content: any) => void): BookState => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<BookContent[]>([]);
    const [selectedBook, setSelectedBook] = useState<BookContent | undefined>(undefined);
    const [error, setError] = useState('');
    const [preserveAspectRatio, setPreserveAspectRatio] = useState<boolean>(false);
    const [editedWidget] = useEditedWidget();

    // Memoize the onBookSelect callback to prevent infinite loops
    const memoizedOnBookSelect = useCallback(onBookSelect, [onBookSelect]);

    // Initialize preserve aspect ratio from edited widget if available
    useEffect(() => {
        if (editedWidget?.content?.aspectRatio) {
            setPreserveAspectRatio(editedWidget.content.aspectRatio === 'original');
        } else {
            // Reset to false for new widgets
            setPreserveAspectRatio(false);
        }
    }, [editedWidget]);

    const searchBooks = async () => {
        if (!searchTerm.trim()) return;
        setIsSearching(true);
        setSearchResults([]);
        setError('');
        try {
            const res = await fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(searchTerm)}`);
            const data = await res.json();
            const books: BookContent[] = (data.docs || []).map((item: any) => ({
                title: item.title,
                author: (item.author_name || []).join(', '),
                coverUrl: item.cover_i ? `https://covers.openlibrary.org/b/id/${item.cover_i}-M.jpg` : undefined,
                infoUrl: item.key ? `https://openlibrary.org${item.key}` : undefined,
                publishedDate: item.first_publish_year ? String(item.first_publish_year) : undefined,
                coverEditionKey: item.cover_edition_key,
                source: 'Open Library'
            }));
            setSearchResults(books);
        } catch (e) {
            setError('No results found');
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const clearSearchResults = () => {
        setSearchResults([]);
        setError('');
        setSelectedBook(undefined);
    };

    const selectBook = useCallback(
        (book: BookContent) => {
            setSelectedBook(book);
            // Use consistent as default, original when preserveAspectRatio is true
            const finalAspectRatio = preserveAspectRatio ? 'original' : 'consistent';
            memoizedOnBookSelect({ ...book, aspectRatio: finalAspectRatio });
        },
        [preserveAspectRatio, memoizedOnBookSelect]
    );

    useEffect(() => {
        const fetchIsbn = async () => {
            if (!selectedBook) return;

            try {
                const isbnRes = await fetch(`https://openlibrary.org/books/${selectedBook.coverEditionKey}.json`);
                const isbnData = await isbnRes.json();
                const isbn = isbnData.isbn_13?.[0] || isbnData.isbn_10?.[0];

                // Use consistent as default, original when preserveAspectRatio is true
                const finalAspectRatio = preserveAspectRatio ? 'original' : 'consistent';
                memoizedOnBookSelect({ ...selectedBook, isbn, aspectRatio: finalAspectRatio });
            } catch (err) {
                // Use consistent as default, original when preserveAspectRatio is true
                const finalAspectRatio = preserveAspectRatio ? 'original' : 'consistent';
                memoizedOnBookSelect({ ...selectedBook, isbn: 'No ISBN found', aspectRatio: finalAspectRatio });
            }
        };

        fetchIsbn();
    }, [selectedBook, preserveAspectRatio, memoizedOnBookSelect]);

    // Handle aspect ratio changes for edited widgets
    useEffect(() => {
        if (editedWidget?.content) {
            // Use consistent as default, original when preserveAspectRatio is true
            const finalAspectRatio = preserveAspectRatio ? 'original' : 'consistent';
            memoizedOnBookSelect({
                ...editedWidget.content,
                aspectRatio: finalAspectRatio
            });
        }
    }, [editedWidget?.content, preserveAspectRatio, memoizedOnBookSelect]);

    return {
        searchTerm,
        setSearchTerm,
        isSearching,
        searchResults,
        selectBook,
        selectedBook,
        searchBooks,
        clearSearchResults,
        error,
        setError,
        preserveAspectRatio,
        setPreserveAspectRatio
    };
};
